import { useEffect, useRef } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import * as api from "@/services/orchardApiService";
import { demoSummary, demoSeasons, demoForecast } from "@/services/demoAdapter";
import { OutOfCoverageError } from "@/services/orchardDataService";
import { CONFIDENCE_CONFIG } from "@/config/confidence";
import { STATIC_STATION_VMS } from "@/data/stations";
import { distanceMi } from "@/lib/geo";
import { useDataMode } from "@/context/DataModeContext";

export const selectionKey = (s) => (s.type === "station" ? `st-${s.stationId}` : `${s.lat.toFixed(4)},${s.lng.toFixed(4)}`);

const retry = (count, err) => err?.name !== "OutOfCoverageError" && (err?.status ?? 0) < 400 && count < 1;

const assertCoverage = (sel, stations) => {
  const nearest = Math.min(...stations.map((s) => distanceMi({ lat: sel.lat, lng: sel.lng }, s)));
  if (nearest > CONFIDENCE_CONFIG.coverageLimitMi) throw new OutOfCoverageError(nearest);
};

export const useHealth = () => {
  const { mode } = useDataMode();
  return useQuery({
    queryKey: ["health"],
    queryFn: ({ signal }) => api.fetchHealth(signal),
    enabled: mode === "live",
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    retry: 1,
  });
};

// When the backend swaps in a new snapshot (new issue date or refresh time), refetch every data query.
export const useLiveDataSync = () => {
  const qc = useQueryClient();
  const health = useHealth();
  const stamp = health.data ? `${health.data.issueDate}|${health.data.refreshedAt}` : null;
  const prev = useRef(null);
  useEffect(() => {
    if (!stamp) return;
    if (prev.current && prev.current !== stamp) {
      qc.invalidateQueries({ predicate: (q) => !["health", "stations"].includes(q.queryKey[0]) });
      toast.success("New CIMIS data loaded", { description: `Observations through ${health.data.issueDate}` });
    }
    prev.current = stamp;
  }, [stamp, qc, health.data]);
  return health;
};

export const useStations = () => {
  const { mode } = useDataMode();
  return useQuery({
    queryKey: ["stations", mode],
    queryFn: ({ signal }) => (mode === "live" ? api.fetchStations(signal) : STATIC_STATION_VMS),
    staleTime: Infinity,
    retry: 1,
  });
};

export const useOrchardSummary = (sel, stations) => {
  const { mode } = useDataMode();
  return useQuery({
    queryKey: ["summary", mode, selectionKey(sel)],
    queryFn: ({ signal }) => {
      if (mode === "demo") return demoSummary(sel);
      if (sel.type === "custom") {
        assertCoverage(sel, stations);
        return api.fetchPointSummary(sel.lat, sel.lng, stations, signal);
      }
      const st = stations.find((s) => s.id === sel.stationId);
      if (!st) throw new api.ApiError(`Unknown station ${sel.stationId}`, 404);
      return api.fetchStationSummary(st, signal);
    },
    enabled: !!stations,
    retry,
  });
};

export const useHistoricalSeasons = (sel, metric) => {
  const { mode } = useDataMode();
  const supported = mode === "demo" || sel.type === "station";
  return useQuery({
    queryKey: ["seasons", mode, selectionKey(sel), metric],
    queryFn: ({ signal }) => (mode === "demo" ? demoSeasons(sel, metric) : api.fetchSeasons(sel.stationId, metric, signal)),
    enabled: supported,
    placeholderData: keepPreviousData,
    retry,
  });
};

export const useForecastRows = (sel) => {
  const { mode } = useDataMode();
  return useQuery({
    queryKey: ["forecast-rows", selectionKey(sel)],
    queryFn: ({ signal }) => api.fetchForecastRows(sel.stationId, signal),
    enabled: mode === "live" && sel.type === "station",
    placeholderData: keepPreviousData,
    retry,
  });
};

export const useDemoForecast = (sel, horizon) => {
  const { mode } = useDataMode();
  return useQuery({
    queryKey: ["demo-forecast", selectionKey(sel), horizon],
    queryFn: () => demoForecast(sel, horizon),
    enabled: mode === "demo",
    placeholderData: keepPreviousData,
    retry,
  });
};

export const useOrchardMap = (metric, value) => {
  const { mode } = useDataMode();
  return useQuery({
    queryKey: ["map", metric, value],
    queryFn: ({ signal }) => api.fetchMapGrid(metric, value, signal),
    enabled: mode === "live",
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry,
  });
};
